import { ConfigStore } from '@sureinc/atlas-common/config';

import { isRefreshableAuthStatus } from '@/utils/authStatus';
import { isUpperEnvironments } from '@/utils/env.utils';
import { removeIdentifierFromObject } from '@/utils/user.utils';

export const isLogRocketEnabled =
  import.meta.env.VITE_LOGROCKET_ENABLED === 'true' &&
  !!import.meta.env.VITE_LOGROCKET_APPID;

export type AuthRequestAttempt = 'initial' | 'retry';

type AuthAttemptMarkerId = number;

interface AuthRequestAttemptMarker {
  url: string;
  method: string;
  attempt: AuthRequestAttempt;
}

interface CapturedRequest {
  reqId: string;
  url: string;
  // LogRocket omits the method for fetch() calls that use the GET default.
  method?: string;
}

interface PendingAttempt {
  id: AuthAttemptMarkerId;
  attempt: AuthRequestAttempt;
}

export interface AuthTelemetryTracker {
  beginAttempt(marker: AuthRequestAttemptMarker): AuthAttemptMarkerId;
  endAttempt(markerId: AuthAttemptMarkerId): void;
  captureRequest(request: CapturedRequest): void;
  consumeResponse(reqId: string): AuthRequestAttempt | undefined;
}

function getRequestKey({
  url,
  method,
}: {
  url: string;
  method?: string;
}): string {
  return `${(method ?? 'GET').toUpperCase()} ${url}`;
}

/**
 * Correlates request-layer auth attempts with LogRocket request IDs without
 * sending an internal marker header to the backend. Callers remove a marker
 * immediately after fetch() returns; only a synchronously captured request can
 * consume it, so an uncaptured request cannot shift a later request's label.
 */
export function createAuthTelemetryTracker(): AuthTelemetryTracker {
  const pendingAttempts = new Map<string, PendingAttempt[]>();
  const markerKeys = new Map<AuthAttemptMarkerId, string>();
  const attemptsByRequestId = new Map<string, AuthRequestAttempt>();
  let nextMarkerId = 1;

  return {
    beginAttempt(marker) {
      const key = getRequestKey(marker);
      const id = nextMarkerId;
      nextMarkerId += 1;
      const attempts = pendingAttempts.get(key) ?? [];
      attempts.push({ id, attempt: marker.attempt });
      pendingAttempts.set(key, attempts);
      markerKeys.set(id, key);
      return id;
    },
    endAttempt(markerId) {
      const key = markerKeys.get(markerId);
      if (!key) return;

      const attempts = pendingAttempts.get(key);
      const remainingAttempts = attempts?.filter(({ id }) => id !== markerId);
      if (remainingAttempts?.length) {
        pendingAttempts.set(key, remainingAttempts);
      } else {
        pendingAttempts.delete(key);
      }
      markerKeys.delete(markerId);
    },
    captureRequest(request) {
      const key = getRequestKey(request);
      const attempts = pendingAttempts.get(key);
      const pendingAttempt = attempts?.shift();

      if (!pendingAttempt) return;
      if (attempts?.length === 0) pendingAttempts.delete(key);
      markerKeys.delete(pendingAttempt.id);
      attemptsByRequestId.set(request.reqId, pendingAttempt.attempt);
    },
    consumeResponse(reqId) {
      const attempt = attemptsByRequestId.get(reqId);
      attemptsByRequestId.delete(reqId);
      return attempt;
    },
  };
}

const authTelemetryTracker = createAuthTelemetryTracker();
let authTelemetryReady = false;

export function withAuthRequestTelemetry<T>(
  marker: AuthRequestAttemptMarker,
  executeRequest: () => T
): T {
  if (!isLogRocketEnabled || !authTelemetryReady) return executeRequest();

  const markerId = authTelemetryTracker.beginAttempt(marker);
  try {
    return executeRequest();
  } finally {
    authTelemetryTracker.endAttempt(markerId);
  }
}

export function shouldSuppressAuthResponse(
  status: number | undefined,
  attempt: AuthRequestAttempt | undefined
): boolean {
  return attempt === 'initial' && isRefreshableAuthStatus(status);
}

export function reportTerminalAuthRecovery({
  status,
  method,
  reason,
}: {
  status: number;
  method: string;
  reason: 'refresh-error' | 'refresh-unavailable' | 'interaction-required';
}): void {
  if (!isLogRocketEnabled) return;

  void import('logrocket').then(({ default: LogRocket }) => {
    LogRocket.captureMessage('Authentication recovery failed', {
      tags: {
        category: 'authentication-recovery',
        method,
        reason,
        status,
      },
    });
  });
}

export const initLogRocket = () => {
  if (!isLogRocketEnabled) return;

  import('logrocket').then(({ default: LogRocket }) => {
    import('logrocket-react').then(
      // @ts-expect-error bad types on the logrocket team
      ({ default: { default: setupLogRocketReact } }) => {
        LogRocket.init(import.meta.env.VITE_LOGROCKET_APPID, {
          release: `${__APP_VERSION__}-${ConfigStore.config.environment}`,
          network: {
            requestSanitizer(request) {
              authTelemetryTracker.captureRequest(request);

              const sanitizedRequest: typeof request = {
                ...request,
                headers: { ...request.headers },
              };

              if (
                isUpperEnvironments &&
                sanitizedRequest.headers.Authorization
              ) {
                sanitizedRequest.headers.Authorization = 'Bearer [REDACTED]';
              }

              return {
                ...sanitizedRequest,
                body: removeIdentifierFromObject(
                  sanitizedRequest.body as string
                ),
              };
            },
            responseSanitizer(response) {
              const authAttempt = authTelemetryTracker.consumeResponse(
                response.reqId
              );

              if (response.status === 0) return null;

              // Initial 401/403 responses are expected refresh input. If no
              // retry can be made, reportTerminalAuthRecovery records the
              // terminal outcome after the request layer knows recovery failed.
              if (shouldSuppressAuthResponse(response.status, authAttempt)) {
                return null;
              }

              if (isUpperEnvironments) {
                return {
                  ...response,
                  body: removeIdentifierFromObject(response.body as string),
                };
              }

              return response;
            },
          },
          console: {
            shouldAggregateConsoleErrors: true,
          },
          shouldDebugLog: false,
        });
        authTelemetryReady = true;

        const originalCaptureException =
          LogRocket.captureException?.bind(LogRocket);
        if (originalCaptureException) {
          const filteredCaptureException: typeof LogRocket.captureException = (
            error,
            opts
          ) => {
            // Suppress ViewTransition skipTransition() noise from React Router.
            // Terminal 401/403 exceptions remain observable; recovered initial
            // responses never throw and are filtered by responseSanitizer.
            if (
              error instanceof Error &&
              error.message?.includes('skipTransition')
            ) {
              return;
            }
            originalCaptureException(error, opts);
          };
          LogRocket.captureException = filteredCaptureException;
        }

        setupLogRocketReact(LogRocket);
      }
    );
  });
};
